import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { ReactSVG } from "react-svg";

import Button from "../../../../common/Button";
import { updateInterventionAssessment } from "../../../../../redux/modules/intervention/actions";
import { InterventionStepsEnum } from "../../../text/data";
import { useStepNavigation } from "./useStepNavigation";
import { DocumentationFormsCard } from "../components";
import {
  getInterventionCompleteSessionKey,
  INTERVENTION_ACKNOWLEDGED_EVENT,
} from "../data";

/**
 * Final review / wrap-up step. Reached from Billing once Documentation and
 * Billing are both complete; it is deliberately a step forward rather than a
 * return to Documentation, which keeps its own content and completed state.
 */
const Summary = ({
  intervention,
  currentInterventionSteps,
  currentStep: activeStep,
  onNavigate,
}) => {
  const dispatch = useDispatch();
  const { interventionId } = useParams();
  const { previousStep } = useStepNavigation(
    InterventionStepsEnum.Summary,
    currentInterventionSteps,
    activeStep
  );

  const isSessionStorageAvailable = typeof sessionStorage !== "undefined";
  // Acknowledging completion is a per-session UI state, like the other
  // intervention wrap-up flags; the record itself is already Completed.
  const completionSessionKey = getInterventionCompleteSessionKey(interventionId);

  const [isCompletionAcknowledged, setIsCompletionAcknowledged] = useState(false);

  useEffect(() => {
    if (!completionSessionKey || !isSessionStorageAvailable) {
      setIsCompletionAcknowledged(false);
      return;
    }
    setIsCompletionAcknowledged(
      sessionStorage.getItem(completionSessionKey) === "1"
    );
  }, [completionSessionKey, isSessionStorageAvailable]);

  const handleCompleteIntervention = () => {
    setIsCompletionAcknowledged(true);

    if (completionSessionKey && isSessionStorageAvailable) {
      sessionStorage.setItem(completionSessionKey, "1");
    }

    if (typeof window !== "undefined" && intervention?.patientId && interventionId) {
      const detail = { patientId: intervention.patientId, interventionId };
      // Tell the landscape list to show the completed checkmark, then bring the
      // sidebar back to the interventions view.
      window.dispatchEvent(
        new CustomEvent(INTERVENTION_ACKNOWLEDGED_EVENT, { detail })
      );
      window.dispatchEvent(
        new CustomEvent("intervention-sidebar-focus-interventions", { detail })
      );
    }
  };

  const handleBack = async () => {
    try {
      // Persist first; only move to the previous tab once the save succeeds.
      const result = await dispatch(
        updateInterventionAssessment(interventionId, {
          currentStep: previousStep,
        })
      );
      if (result) {
        onNavigate?.(previousStep);
      }
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  return (
    <>
      <div className="w-full max-h-fit overflow-auto">
        <div className="w-full h-full flex flex-col py-3">
          <div className="flex gap-8 flex-1">
            <DocumentationFormsCard intervention={intervention} />
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between space-x-4">
        <div className="w-full">{/* Progress indicator could go here */}</div>
        <div className="flex items-center gap-3">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={handleBack}
          />
          {isCompletionAcknowledged ? (
            <div className="flex items-center gap-3 px-6 py-6 text-REELTeal font-bold text-[20px]">
              <span className="mt-4">Completed</span>
              <ReactSVG
                src="/icons/medication-card-complete.svg"
                className="w-8 h-8"
              />
            </div>
          ) : (
            <Button
              type="primary"
              text="Complete Intervention"
              className="w-[270px] max-h-[52px] !rounded-[30px]"
              onClick={handleCompleteIntervention}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Summary;
